import { get as getFingerprint } from '@jusbrasil-web/auth/fingerprint-collector'
import { useCallback } from 'react'

import { externalAuthApi } from '../api'
import { DEFAULT_ERROR_MESSAGE } from '../constants'
import { storePasswordCredential } from '../utils'
import { type UseAuthBaseOptions, useAuthBase } from './useAuthBase'
import { useEnsureAuthServerCsrf } from './useEnsureAuthServerCsrf'

// Best-effort capture of browser-only fingerprint signals to ship in the
// POST /login body. Fail-open: if the collector is not initialized, throws,
// or any signal getter blows up, we proceed without signals — login must
// never fail because of fingerprinting.
function collectFingerprintSignals(): Record<string, string> | undefined {
  try {
    return getFingerprint().signals
  } catch {
    return undefined
  }
}

type UseLoginOptions = UseAuthBaseOptions & {
  onAuthSuccess?: () => void
}

interface UseLoginReturn {
  email: string
  isLoading: boolean
  error: string | undefined
  handlePasswordSubmit: (password: string) => Promise<void>
}

export function useLogin(options: UseLoginOptions = {}): UseLoginReturn {
  const { nextUrl, initialEmail, onAuthSuccess } = options
  const email = initialEmail || ''
  const { isLoading, error, setError, buildEventData, redirectToCallback, handleAsyncOperation } =
    useAuthBase(options)
  const { ensureAuthServerCsrf } = useEnsureAuthServerCsrf('rollout')

  const handlePasswordSubmit = useCallback(
    async (password: string) => {
      const fingerprintSignals = collectFingerprintSignals()
      await ensureAuthServerCsrf()
      const result = await handleAsyncOperation(async () => {
        return await externalAuthApi.signInWithEmail({
          email,
          password,
          next_url: nextUrl,
          event_data: buildEventData('LogInModal'),
          fingerprint_signals: fingerprintSignals,
        })
      })

      if (!result) {
        return
      }

      const { response, data } = result

      if (!response.ok) {
        console.warn('[Auth:Login] Login failed:', {
          status: response.status,
        })

        if (response.status === 409 && data?.mustRedirectTo) {
          redirectToCallback(data.mustRedirectTo)
          return
        }

        setError(data?.message || DEFAULT_ERROR_MESSAGE)
        return
      }

      await storePasswordCredential(email, password)

      if (onAuthSuccess) {
        onAuthSuccess()
        return
      }

      if (data?.nextUrl) {
        redirectToCallback(data.nextUrl)
        return
      }

      redirectToCallback('/')
    },
    [
      handleAsyncOperation,
      email,
      nextUrl,
      buildEventData,
      setError,
      redirectToCallback,
      onAuthSuccess,
      ensureAuthServerCsrf,
    ]
  )

  return {
    email,
    isLoading,
    error,
    handlePasswordSubmit,
  }
}
